import { useState, useEffect } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Copy, Mail, MessageSquare, Send, Link2 } from "lucide-react";
import { ComplaintTemplates } from "@/components/ComplaintTemplates";

export function ComplaintShare({ complaintId }) {
  const [info, setInfo] = useState(null);
  const [emailTo, setEmailTo] = useState("");
  const [emailSubject, setEmailSubject] = useState("");
  const [emailBody, setEmailBody] = useState("");
  const [smsTo, setSmsTo] = useState("");
  const [smsBody, setSmsBody] = useState("");
  const [waTo, setWaTo] = useState("");
  const [waBody, setWaBody] = useState("");
  const [sending, setSending] = useState("");

  useEffect(() => {
    api.get(`/complaints/${complaintId}/share-info`).then((r) => {
      const d = r.data; setInfo(d);
      setEmailTo(d.email || ""); setEmailSubject(d.default_email_subject || ""); setEmailBody(d.default_email_body || "");
      setSmsTo(d.mobile || ""); setSmsBody(d.default_sms || "");
      setWaTo(d.mobile || ""); setWaBody(d.default_sms || "");
    }).catch(() => {});
  }, [complaintId]);

  if (!info) return null;

  const copy = () => { navigator.clipboard.writeText(info.url); toast.success("Link gekopieerd"); };
  const sendEmail = async () => {
    if (!emailTo.includes("@")) { toast.error("Vul een geldig e-mailadres in"); return; }
    setSending("email");
    try { const r = await api.post(`/complaints/${complaintId}/share`, { channel: "email", to: emailTo, subject: emailSubject, body: emailBody }); toast.success(r.data.mocked ? "E-mail gesimuleerd (geen provider gekoppeld)" : "E-mail verzonden"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Versturen mislukt"); }
    finally { setSending(""); }
  };
  const sendSms = async () => {
    if (!smsTo.trim()) { toast.error("Vul een mobiel nummer in"); return; }
    setSending("sms");
    try { const r = await api.post(`/complaints/${complaintId}/share`, { channel: "sms", to: smsTo, body: smsBody }); toast.success(r.data.mocked ? "SMS gesimuleerd (Twilio niet gekoppeld)" : "SMS verzonden"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Versturen mislukt"); }
    finally { setSending(""); }
  };
  const openWhatsapp = () => {
    const num = (waTo || "").replace(/[^0-9]/g, "");
    if (!num) { toast.error("Vul een mobiel nummer in"); return; }
    window.open(`https://wa.me/${num}?text=${encodeURIComponent(waBody)}`, "_blank");
  };

  return (
    <div className="space-y-3" data-testid="complaint-share">
      <div>
        <Label className="text-xs flex items-center gap-1"><Link2 className="h-3.5 w-3.5" /> Portal-link voor debiteur</Label>
        <div className="flex items-center gap-2 mt-1">
          <Input readOnly value={info.url} className="text-xs" data-testid="share-link-input" />
          <Button variant="outline" size="sm" onClick={copy} data-testid="share-copy-btn"><Copy className="h-4 w-4" /></Button>
        </div>
      </div>

      <div className="border rounded-xl p-3 space-y-2 bg-slate-50/60">
        <Label className="text-xs flex items-center gap-1 text-slate-600"><Mail className="h-3.5 w-3.5" /> Versturen via e-mail</Label>
        <Input value={emailTo} onChange={(e) => setEmailTo(e.target.value)} placeholder="E-mailadres debiteur" data-testid="share-email-to" />
        <Input value={emailSubject} onChange={(e) => setEmailSubject(e.target.value)} placeholder="Onderwerp" data-testid="share-email-subject" />
        <Textarea rows={4} value={emailBody} onChange={(e) => setEmailBody(e.target.value)} data-testid="share-email-body" />
        <ComplaintTemplates scope="share_email" testId="share-email-templates" clientId={info.client_id} current={{ subject: emailSubject, body: emailBody }} onApply={(t) => { if (t.subject) setEmailSubject(t.subject); if (t.body) setEmailBody(t.body); }} />
        <Button className="w-full bg-blue-600 hover:bg-blue-700" disabled={sending === "email"} onClick={sendEmail} data-testid="share-email-send"><Send className="h-4 w-4 mr-2" /> {sending === "email" ? "Versturen…" : "E-mail versturen"}</Button>
      </div>

      <div className="border rounded-xl p-3 space-y-2 bg-slate-50/60">
        <Label className="text-xs flex items-center gap-1 text-slate-600"><MessageSquare className="h-3.5 w-3.5" /> Versturen via SMS</Label>
        <Input value={smsTo} onChange={(e) => setSmsTo(e.target.value)} placeholder="Mobiel nummer" data-testid="share-sms-to" />
        <Textarea rows={3} value={smsBody} onChange={(e) => setSmsBody(e.target.value)} data-testid="share-sms-body" />
        <ComplaintTemplates scope="share_sms" testId="share-sms-templates" clientId={info.client_id} current={{ body: smsBody }} onApply={(t) => { if (t.body) setSmsBody(t.body); }} />
        <Button className="w-full bg-indigo-600 hover:bg-indigo-700" disabled={sending === "sms"} onClick={sendSms} data-testid="share-sms-send"><Send className="h-4 w-4 mr-2" /> {sending === "sms" ? "Versturen…" : "SMS versturen"}</Button>
      </div>

      <div className="border rounded-xl p-3 space-y-2 bg-slate-50/60">
        <Label className="text-xs flex items-center gap-1 text-emerald-700"><MessageSquare className="h-3.5 w-3.5" /> Versturen via WhatsApp</Label>
        <Input value={waTo} onChange={(e) => setWaTo(e.target.value)} placeholder="Mobiel nummer" data-testid="share-wa-to" />
        <Textarea rows={3} value={waBody} onChange={(e) => setWaBody(e.target.value)} data-testid="share-wa-body" />
        <ComplaintTemplates scope="share_sms" testId="share-wa-templates" clientId={info.client_id} current={{ body: waBody }} onApply={(t) => { if (t.body) setWaBody(t.body); }} />
        <Button className="w-full bg-emerald-600 hover:bg-emerald-700" onClick={openWhatsapp} data-testid="share-wa-send"><Send className="h-4 w-4 mr-2" /> WhatsApp openen (webversie)</Button>
      </div>
    </div>
  );
}
